/**
 * Analytics — thin wrapper around posthog-js.
 *
 * Mirrors the Sentry setup in main.tsx: PostHog only initializes when
 * VITE_POSTHOG_KEY is present at build time. Locally and in PR previews
 * (no key set), every function here is a safe no-op and nothing is sent.
 *
 * Privacy posture (beta):
 *   - EU data region (Frankfurt) by default.
 *   - Session replay is ON. The editor surface is intentionally NOT masked
 *     so we can see how writers actually use the core product. Form <input>
 *     fields stay masked (PostHog default). This is acceptable while testers
 *     are non-paying prospects who are told (at onboarding) that sessions are
 *     recorded and to avoid pasting confidential content.
 *   - Plan: once a tester converts to a paying customer, replay should be
 *     disabled (or scoped to solo/freelance tiers) while event capture stays
 *     on. That gate depends on a `plan_tier` field that does not exist yet
 *     (no Stripe / billing model) — deferred.
 */
import posthog from 'posthog-js'
import type { AuthUser } from '@/lib/auth'

const KEY = import.meta.env.VITE_POSTHOG_KEY
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com'

/** True once init() has actually run (i.e. a key was provided). */
let enabled = false

/** Initialize PostHog once at app bootstrap. No-op when no key is configured. */
export function initAnalytics(): void {
  if (enabled || !KEY) return
  posthog.init(KEY, {
    api_host: HOST,
    persistence: 'localStorage',
    capture_pageview: true,
    // Session replay on. Editor content is left visible on purpose (see file
    // header); form inputs are masked by PostHog's defaults.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: { password: true, email: true },
    },
  })
  enabled = true
}

/** Link the current session to the authenticated user. */
export function identifyUser(user: AuthUser): void {
  if (!enabled) return
  posthog.identify(user.id, { email: user.email })
}

/** Clear identity on logout so sessions don't bleed across accounts. */
export function resetUser(): void {
  if (!enabled) return
  posthog.reset()
}

/** Capture a product event. No-op when analytics is disabled. */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!enabled) return
  posthog.capture(event, properties)
}
